import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/internal/operators';
import {IStock} from '../../shared/model/stock.model';
import {IStockMovement} from '../../shared/model/stock-movement.model';
import {SERVER_API_URL} from '../../app.constants';
import {createRequestOption} from '../../shared/util/request-util';


type EntityResponseType = HttpResponse<IStock>;
type EntityArrayResponseType = HttpResponse<IStock[]>;
type MovementArrayResponseType = HttpResponse<IStockMovement[]>;

@Injectable({providedIn: 'root'})
export class StockService {
  public resourceUrl = SERVER_API_URL + 'api/stocks';

  constructor(protected http: HttpClient) {
  }

  create(stock: IStock): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(stock);
    return this.http
      .post<IStock>(this.resourceUrl, copy, {observe: 'response'})
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  update(stock: IStock): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(stock);
    return this.http
      .put<IStock>(this.resourceUrl, copy, {observe: 'response'})
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  find(id: number): Observable<EntityResponseType> {
    return this.http
      .get<IStock>(`${this.resourceUrl}/${id}`, {observe: 'response'})
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<IStock[]>(this.resourceUrl, {params: options, observe: 'response'})
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  findMovements(stockId: number, req?: any): Observable<MovementArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<IStockMovement[]>(`${this.resourceUrl}/${stockId}/movements`, {
      params: options,
      observe: 'response'
    });
  }

  findAllMovements(req?: any): Observable<MovementArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<IStockMovement[]>(`${this.resourceUrl}-movements`, {
      params: options,
      observe: 'response'
    });
  }

  getOrderPoint(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<IStock[]>(this.resourceUrl + '-order-point', {params: options, observe: 'response'})
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  getAlarmPoint(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<IStock[]>(this.resourceUrl + '-alarm-point', {params: options, observe: 'response'})
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  findMyStockCount(id: number): Observable<HttpResponse<any>> {
    return this.http
      .get<any>(`${this.resourceUrl + '-self-count'}/${id}`, {observe: 'response'});
  }

  findMyStock(id: number): Observable<EntityArrayResponseType> {
    return this.http
      .get<IStock[]>(`${this.resourceUrl + '-self'}/${id}`, {observe: 'response'})
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  findStockTransaction(id: number): Observable<EntityArrayResponseType> {
    return this.http
      .get<IStock[]>(`${this.resourceUrl + '-transaction'}/${id}`, {observe: 'response'})
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  findWithCustomerId(id: number): Observable<EntityArrayResponseType> {
    return this.http
      .get<IStock[]>(`${this.resourceUrl + '-customer'}/${id}`, {observe: 'response'})
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  delete(id: number): Observable<HttpResponse<any>> {
    return this.http.delete<any>(`${this.resourceUrl}/${id}`, {observe: 'response'});
  }

  protected convertDateFromClient(stock: IStock): IStock {
    return Object.assign({}, stock, {});
  }

  countAlarmPoint(): Observable<HttpResponse<any>> {
    return this.http
      .get<any>(`${this.resourceUrl + '-count-alarm-point'}`, {observe: 'response'});
  }

  countOrderPoint(): Observable<HttpResponse<any>> {
    return this.http
      .get<any>(`${this.resourceUrl + '-count-order-point'}`, {observe: 'response'});
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    return res;
  }
}
